/**
 * Mapea timezones IANA a códigos ISO2 de países para mostrar banderas.
 * 
 * @param timezone - Timezone en formato IANA (ej: "America/Argentina/Buenos_Aires")
 * @returns Código ISO2 del país (ej: "AR") o null si no se puede determinar
 * 
 * @example
 * timezoneToCountryCode("America/Argentina/Buenos_Aires") // "AR"
 * timezoneToCountryCode("Europe/Madrid") // "ES"
 * timezoneToCountryCode("America/New_York") // "US"
 */
export function timezoneToCountryCode(timezone?: string | null): string | null {
  if (!timezone) return null;

  const normalized = timezone.trim();
  if (!normalized) return null;

  // Mapeo exhaustivo de timezones IANA a códigos ISO2
  const timezoneMap: Record<string, string> = {
    // América - Argentina
    'America/Argentina/Buenos_Aires': 'AR',
    'America/Argentina/Cordoba': 'AR',
    'America/Argentina/Salta': 'AR',
    'America/Argentina/Jujuy': 'AR',
    'America/Argentina/Tucuman': 'AR',
    'America/Argentina/Catamarca': 'AR',
    'America/Argentina/La_Rioja': 'AR',
    'America/Argentina/San_Juan': 'AR',
    'America/Argentina/Mendoza': 'AR',
    'America/Argentina/San_Luis': 'AR',
    'America/Argentina/Rio_Gallegos': 'AR',
    'America/Argentina/Ushuaia': 'AR',
    // Aliases legacy de Argentina (formato sin /Argentina/)
    'America/Buenos_Aires': 'AR',
    'America/Cordoba': 'AR',
    'America/Rosario': 'AR',
    'America/Jujuy': 'AR',
    'America/Catamarca': 'AR',
    'America/Mendoza': 'AR',

    // América - Brasil
    'America/Sao_Paulo': 'BR',
    'America/Manaus': 'BR',
    'America/Campo_Grande': 'BR',
    'America/Cuiaba': 'BR',
    'America/Belem': 'BR',
    'America/Fortaleza': 'BR',
    'America/Recife': 'BR',
    'America/Araguaina': 'BR',
    'America/Maceio': 'BR',
    'America/Bahia': 'BR',
    'America/Noronha': 'BR',
    'America/Rio_Branco': 'BR',
    'America/Porto_Velho': 'BR',
    'America/Boa_Vista': 'BR',

    // América - Chile
    'America/Santiago': 'CL',
    'America/Punta_Arenas': 'CL',
    'America/Easter': 'CL',

    // América - Colombia
    'America/Bogota': 'CO',

    // América - México
    'America/Mexico_City': 'MX',
    'America/Cancun': 'MX',
    'America/Merida': 'MX',
    'America/Monterrey': 'MX',
    'America/Mazatlan': 'MX',
    'America/Chihuahua': 'MX',
    'America/Hermosillo': 'MX',
    'America/Tijuana': 'MX',
    'America/Bahia_Banderas': 'MX',

    // América - Estados Unidos
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Denver': 'US',
    'America/Los_Angeles': 'US',
    'America/Anchorage': 'US',
    'America/Phoenix': 'US',
    'America/Detroit': 'US',
    'America/Indianapolis': 'US',
    'America/Louisville': 'US',
    'America/New_Orleans': 'US',
    'America/Boise': 'US',
    'America/Juneau': 'US',
    'America/Sitka': 'US',
    'America/Metlakatla': 'US',
    'America/Yakutat': 'US',
    'America/Nome': 'US',
    'America/Adak': 'US',
    'America/Honolulu': 'US',
    'America/Menominee': 'US',
    'America/North_Dakota/Center': 'US',
    'America/North_Dakota/New_Salem': 'US',
    'America/North_Dakota/Beulah': 'US',

    // América - Canadá
    'America/Toronto': 'CA',
    'America/Vancouver': 'CA',
    'America/Edmonton': 'CA',
    'America/Winnipeg': 'CA',
    'America/Halifax': 'CA',
    'America/St_Johns': 'CA',
    'America/Regina': 'CA',
    'America/Whitehorse': 'CA',
    'America/Dawson': 'CA',
    'America/Inuvik': 'CA',
    'America/Iqaluit': 'CA',
    'America/Moncton': 'CA',
    'America/Glace_Bay': 'CA',
    'America/Goose_Bay': 'CA',
    'America/Blanc-Sablon': 'CA',
    'America/Montreal': 'CA',
    'America/Thunder_Bay': 'CA',
    'America/Nipigon': 'CA',
    'America/Rainy_River': 'CA',
    'America/Atikokan': 'CA',
    'America/Cambridge_Bay': 'CA',
    'America/Yellowknife': 'CA',
    'America/Creston': 'CA',
    'America/Dawson_Creek': 'CA',
    'America/Fort_Nelson': 'CA',

    // América - Uruguay
    'America/Montevideo': 'UY',

    // América - Paraguay
    'America/Asuncion': 'PY',

    // América - Perú
    'America/Lima': 'PE',

    // América - Ecuador
    'America/Guayaquil': 'EC',
    'Pacific/Galapagos': 'EC',

    // América - Venezuela
    'America/Caracas': 'VE',

    // América - Bolivia
    'America/La_Paz': 'BO',

    // América - Panamá
    'America/Panama': 'PA',

    // América - Costa Rica
    'America/Costa_Rica': 'CR',

    // América - Guatemala
    'America/Guatemala': 'GT',

    // América - El Salvador
    'America/El_Salvador': 'SV',

    // América - Honduras
    'America/Tegucigalpa': 'HN',

    // América - Nicaragua
    'America/Managua': 'NI',

    // América - Cuba
    'America/Havana': 'CU',

    // América - Jamaica
    'America/Jamaica': 'JM',

    // América - República Dominicana
    'America/Santo_Domingo': 'DO',

    // América - Puerto Rico
    'America/Puerto_Rico': 'PR',

    // América - Haití
    'America/Port-au-Prince': 'HT',

    // América - Trinidad y Tobago
    'America/Port_of_Spain': 'TT',

    // América - Barbados
    'America/Barbados': 'BB',

    // América - Guyana
    'America/Guyana': 'GY',

    // América - Surinam
    'America/Paramaribo': 'SR',

    // América - Guayana Francesa
    'America/Cayenne': 'GF',

    // Europa - España
    'Europe/Madrid': 'ES',
    'Europe/Ceuta': 'ES',
    'Atlantic/Canary': 'ES',

    // Europa - Reino Unido
    'Europe/London': 'GB',
    'Europe/Belfast': 'GB',

    // Europa - Francia
    'Europe/Paris': 'FR',

    // Europa - Alemania
    'Europe/Berlin': 'DE',

    // Europa - Italia
    'Europe/Rome': 'IT',

    // Europa - Portugal
    'Europe/Lisbon': 'PT',
    'Atlantic/Madeira': 'PT',
    'Atlantic/Azores': 'PT',

    // Europa - Países Bajos
    'Europe/Amsterdam': 'NL',

    // Europa - Bélgica
    'Europe/Brussels': 'BE',

    // Europa - Suiza
    'Europe/Zurich': 'CH',

    // Europa - Austria
    'Europe/Vienna': 'AT',

    // Europa - Polonia
    'Europe/Warsaw': 'PL',

    // Europa - República Checa
    'Europe/Prague': 'CZ',

    // Europa - Hungría
    'Europe/Budapest': 'HU',

    // Europa - Rumania
    'Europe/Bucharest': 'RO',

    // Europa - Grecia
    'Europe/Athens': 'GR',

    // Europa - Turquía
    'Europe/Istanbul': 'TR',

    // Europa - Rusia (principales ciudades)
    'Europe/Moscow': 'RU',
    'Europe/Kaliningrad': 'RU',
    'Europe/Volgograd': 'RU',
    'Europe/Samara': 'RU',
    'Europe/Yekaterinburg': 'RU',
    'Europe/Omsk': 'RU',
    'Europe/Krasnoyarsk': 'RU',
    'Europe/Irkutsk': 'RU',
    'Europe/Yakutsk': 'RU',
    'Europe/Vladivostok': 'RU',
    'Europe/Magadan': 'RU',
    'Europe/Kamchatka': 'RU',

    // Europa - Suecia
    'Europe/Stockholm': 'SE',

    // Europa - Noruega
    'Europe/Oslo': 'NO',

    // Europa - Dinamarca
    'Europe/Copenhagen': 'DK',

    // Europa - Finlandia
    'Europe/Helsinki': 'FI',

    // Europa - Irlanda
    'Europe/Dublin': 'IE',

    // Europa - Islandia
    'Atlantic/Reykjavik': 'IS',

    // Asia - Japón
    'Asia/Tokyo': 'JP',

    // Asia - China
    'Asia/Shanghai': 'CN',
    'Asia/Beijing': 'CN',
    'Asia/Hong_Kong': 'HK',
    'Asia/Macau': 'MO',

    // Asia - India
    'Asia/Kolkata': 'IN',
    'Asia/Calcutta': 'IN',

    // Asia - Corea del Sur
    'Asia/Seoul': 'KR',

    // Asia - Corea del Norte
    'Asia/Pyongyang': 'KP',

    // Asia - Singapur
    'Asia/Singapore': 'SG',

    // Asia - Malasia
    'Asia/Kuala_Lumpur': 'MY',

    // Asia - Tailandia
    'Asia/Bangkok': 'TH',

    // Asia - Indonesia
    'Asia/Jakarta': 'ID',
    'Asia/Makassar': 'ID',
    'Asia/Jayapura': 'ID',

    // Asia - Filipinas
    'Asia/Manila': 'PH',

    // Asia - Vietnam
    'Asia/Ho_Chi_Minh': 'VN',

    // Asia - Emiratos Árabes Unidos
    'Asia/Dubai': 'AE',

    // Asia - Arabia Saudí
    'Asia/Riyadh': 'SA',

    // Asia - Israel
    'Asia/Jerusalem': 'IL',
    'Asia/Tel_Aviv': 'IL',

    // Asia - Turquía
    'Asia/Istanbul': 'TR',

    // Oceanía - Australia
    'Australia/Sydney': 'AU',
    'Australia/Melbourne': 'AU',
    'Australia/Brisbane': 'AU',
    'Australia/Perth': 'AU',
    'Australia/Adelaide': 'AU',
    'Australia/Darwin': 'AU',
    'Australia/Hobart': 'AU',

    // Oceanía - Nueva Zelanda
    'Pacific/Auckland': 'NZ',
    'Pacific/Chatham': 'NZ',

    // Otros - UTC
    'UTC': 'ZZ', // UTC no tiene país específico
  };

  // Buscar coincidencia exacta (case-insensitive)
  const exactMatch = timezoneMap[normalized];
  if (exactMatch) return exactMatch;

  // Buscar coincidencia case-insensitive
  const lowerNormalized = normalized.toLowerCase();
  for (const [tz, code] of Object.entries(timezoneMap)) {
    if (tz.toLowerCase() === lowerNormalized) {
      return code;
    }
  }

  // Buscar por prefijo de continente/país (para casos como America/Argentina/*)
  const parts = normalized.split('/');
  if (parts.length >= 2) {
    const continent = parts[0];
    const countryOrCity = parts[1];

    // Mapeos por país en el continente
    const continentCountryMap: Record<string, Record<string, string>> = {
      'America': {
        'Argentina': 'AR',
        'Brazil': 'BR',
        'Brasil': 'BR',
        'Chile': 'CL',
        'Colombia': 'CO',
        'Mexico': 'MX',
        'México': 'MX',
        'United_States': 'US',
        'Canada': 'CA',
        'Uruguay': 'UY',
        'Paraguay': 'PY',
        'Peru': 'PE',
        'Perú': 'PE',
        'Ecuador': 'EC',
        'Venezuela': 'VE',
        'Bolivia': 'BO',
        'Panama': 'PA',
        'Costa_Rica': 'CR',
        'Guatemala': 'GT',
        'El_Salvador': 'SV',
        'Honduras': 'HN',
        'Nicaragua': 'NI',
        'Cuba': 'CU',
        'Jamaica': 'JM',
        'Dominican_Republic': 'DO',
        'Puerto_Rico': 'PR',
        'Haiti': 'HT',
        'Trinidad': 'TT',
        'Barbados': 'BB',
        'Guyana': 'GY',
        'Suriname': 'SR',
      },
      'Europe': {
        'Spain': 'ES',
        'España': 'ES',
        'United_Kingdom': 'GB',
        'France': 'FR',
        'Germany': 'DE',
        'Italy': 'IT',
        'Portugal': 'PT',
        'Netherlands': 'NL',
        'Belgium': 'BE',
        'Switzerland': 'CH',
        'Austria': 'AT',
        'Poland': 'PL',
        'Czech_Republic': 'CZ',
        'Hungary': 'HU',
        'Romania': 'RO',
        'Greece': 'GR',
        'Turkey': 'TR',
        'Russia': 'RU',
        'Sweden': 'SE',
        'Norway': 'NO',
        'Denmark': 'DK',
        'Finland': 'FI',
        'Ireland': 'IE',
      },
      'Asia': {
        'Japan': 'JP',
        'China': 'CN',
        'India': 'IN',
        'South_Korea': 'KR',
        'North_Korea': 'KP',
        'Singapore': 'SG',
        'Malaysia': 'MY',
        'Thailand': 'TH',
        'Indonesia': 'ID',
        'Philippines': 'PH',
        'Vietnam': 'VN',
        'United_Arab_Emirates': 'AE',
        'Saudi_Arabia': 'SA',
        'Israel': 'IL',
        'Turkey': 'TR',
      },
      'Australia': {
        'Sydney': 'AU',
        'Melbourne': 'AU',
        'Brisbane': 'AU',
        'Perth': 'AU',
        'Adelaide': 'AU',
        'Darwin': 'AU',
        'Hobart': 'AU',
      },
      'Pacific': {
        'Auckland': 'NZ',
        'Chatham': 'NZ',
      },
    };

    const continentMap = continentCountryMap[continent];
    if (continentMap) {
      // Buscar por país (case-insensitive)
      const countryMatch = continentMap[countryOrCity];
      if (countryMatch) return countryMatch;

      // Buscar case-insensitive
      const lowerCountry = countryOrCity.toLowerCase();
      for (const [country, code] of Object.entries(continentMap)) {
        if (country.toLowerCase() === lowerCountry) {
          return code;
        }
      }
    }
  }

  // Si no se encuentra ningún mapeo, retornar null
  return null;
}

/**
 * Mapea un nombre de país (en español o inglés) a su código ISO-3166-1 alpha-2.
 * Acepta también el código ISO2 directamente.
 *
 * Cubre los países más relevantes para la plataforma (Latinoamérica, España y
 * otros países con usuarios frecuentes).
 *
 * @returns Código ISO2 en mayúsculas, o null si no se reconoce el nombre.
 */
export function countryNameToISO2(name?: string | null): string | null {
  if (!name) return null;
  const n = name.trim().toLowerCase();
  const map: Record<string, string> = {
    // Argentina
    'argentina': 'AR', 'ar': 'AR',
    // España
    'españa': 'ES', 'espana': 'ES', 'spain': 'ES', 'es': 'ES',
    // México
    'méxico': 'MX', 'mexico': 'MX', 'mx': 'MX',
    // Colombia
    'colombia': 'CO', 'co': 'CO',
    // Chile
    'chile': 'CL', 'cl': 'CL',
    // Perú
    'perú': 'PE', 'peru': 'PE', 'pe': 'PE',
    // Brasil
    'brasil': 'BR', 'brazil': 'BR', 'br': 'BR',
    // Uruguay
    'uruguay': 'UY', 'uy': 'UY',
    // Paraguay
    'paraguay': 'PY', 'py': 'PY',
    // Bolivia
    'bolivia': 'BO', 'bo': 'BO',
    // Ecuador
    'ecuador': 'EC', 'ec': 'EC',
    // Venezuela
    'venezuela': 'VE', 've': 'VE',
    // Panamá
    'panamá': 'PA', 'panama': 'PA', 'pa': 'PA',
    // Costa Rica
    'costa rica': 'CR', 'cr': 'CR',
    // Guatemala
    'guatemala': 'GT', 'gt': 'GT',
    // El Salvador
    'el salvador': 'SV', 'sv': 'SV',
    // Honduras
    'honduras': 'HN', 'hn': 'HN',
    // Nicaragua
    'nicaragua': 'NI', 'ni': 'NI',
    // Cuba
    'cuba': 'CU', 'cu': 'CU',
    // República Dominicana
    'república dominicana': 'DO', 'republica dominicana': 'DO',
    'dominican republic': 'DO', 'do': 'DO',
    // Puerto Rico
    'puerto rico': 'PR', 'pr': 'PR',
    // Estados Unidos
    'estados unidos': 'US', 'united states': 'US', 'usa': 'US', 'us': 'US',
    // Canadá
    'canadá': 'CA', 'canada': 'CA', 'ca': 'CA',
    // Reino Unido
    'reino unido': 'GB', 'united kingdom': 'GB', 'gb': 'GB', 'uk': 'GB',
    // Francia
    'francia': 'FR', 'france': 'FR', 'fr': 'FR',
    // Alemania
    'alemania': 'DE', 'germany': 'DE', 'de': 'DE',
    // Italia
    'italia': 'IT', 'italy': 'IT', 'it': 'IT',
    // Portugal
    'portugal': 'PT', 'pt': 'PT',
    // Países Bajos
    'países bajos': 'NL', 'paises bajos': 'NL', 'netherlands': 'NL', 'nl': 'NL',
    // Suiza
    'suiza': 'CH', 'switzerland': 'CH', 'ch': 'CH',
    // Australia
    'australia': 'AU', 'au': 'AU',
    // Japón
    'japón': 'JP', 'japon': 'JP', 'japan': 'JP', 'jp': 'JP',
    // China
    'china': 'CN', 'cn': 'CN',
    // India
    'india': 'IN', 'in': 'IN',
    // Israel
    'israel': 'IL', 'il': 'IL',
  };
  return map[n] ?? null;
}

/**
 * Convierte un código ISO-3166-1 alpha-2 al nombre del país en español.
 *
 * Cubre los países con presencia en la plataforma.
 *
 * @example
 * countryCodeToName('AR') // 'Argentina'
 * countryCodeToName('ES') // 'España'
 */
export function countryCodeToName(iso2?: string | null): string | null {
  if (!iso2 || iso2.length !== 2) return null;
  const map: Record<string, string> = {
    AR: 'Argentina',
    ES: 'España',
    MX: 'México',
    CO: 'Colombia',
    CL: 'Chile',
    PE: 'Perú',
    BR: 'Brasil',
    UY: 'Uruguay',
    PY: 'Paraguay',
    BO: 'Bolivia',
    EC: 'Ecuador',
    VE: 'Venezuela',
    PA: 'Panamá',
    CR: 'Costa Rica',
    GT: 'Guatemala',
    SV: 'El Salvador',
    HN: 'Honduras',
    NI: 'Nicaragua',
    CU: 'Cuba',
    DO: 'Rep. Dominicana',
    PR: 'Puerto Rico',
    US: 'Estados Unidos',
    CA: 'Canadá',
    GB: 'Reino Unido',
    FR: 'Francia',
    DE: 'Alemania',
    IT: 'Italia',
    PT: 'Portugal',
    NL: 'Países Bajos',
    CH: 'Suiza',
    AU: 'Australia',
    JP: 'Japón',
    CN: 'China',
    IN: 'India',
    IL: 'Israel',
  };
  return map[iso2.toUpperCase()] ?? null;
}


